import type { Section } from '@fourthwall/services/dist/ShopRendererDraftApiClient'

import { useDraftContext } from '../providers/DraftProvider'
import { useCurrentShopContext } from './useCurrentShopContext'
import { useCustomPageLayoutQuery } from './useCustomPageLayoutQuery'
import { usePages } from './usePages'
import { useProductPageLayoutQuery } from './useProductPageLayoutQuery'
import { useSystemPageLayoutQuery } from './useSystemPageLayoutQuery'

export const useUnifiedPageLayoutQuery = () => {
  const { currentShop } = useCurrentShopContext()
  const { currentPage } = usePages()
  const { draftId } = useDraftContext()

  const excludedPages = [
    'checkout',
    'memberships_perks',
    'memberships_feed',
    'memberships_video_catalog',
  ]

  const isQueryEnabled = !!currentPage?.id! && !excludedPages.includes(currentPage.id)

  const { systemPageLayoutQuery } = useSystemPageLayoutQuery(
    [
      {
        shop_id: currentShop.id,
        draft_theme_id: draftId,
        library_page_name: currentPage?.id!,
      },
    ],
    {
      enabled:
        isQueryEnabled && (currentPage?.type === 'Home' || currentPage?.type === 'ComingSoon'),
    },
  )

  const { customPageLayoutQuery } = useCustomPageLayoutQuery(
    [
      {
        shop_id: currentShop.id,
        draft_theme_id: draftId,
        shop_page_id: currentPage?.id!,
      },
    ],
    {
      enabled:
        isQueryEnabled && (currentPage?.type === 'CustomPage' || currentPage?.type === 'Policy'),
    },
  )

  const { productPageLayoutQuery } = useProductPageLayoutQuery(
    [
      {
        shop_id: currentShop.id,
        draft_theme_id: draftId,
        platform_section:
          currentPage?.type === 'ProductPageTemplate' ||
          currentPage?.type === 'CollectionPageTemplate'
            ? currentPage?.platformSection
            : 'product-default',
      },
    ],
    {
      enabled:
        isQueryEnabled &&
        (currentPage?.type === 'ProductPageTemplate' ||
          currentPage?.type === 'CollectionPageTemplate'),
    },
  )

  const pageLayoutQuery = {
    Home: systemPageLayoutQuery,
    ComingSoon: systemPageLayoutQuery,
    CustomPage: customPageLayoutQuery,
    Policy: customPageLayoutQuery,
    ProductPageTemplate: productPageLayoutQuery,
    CollectionPageTemplate: productPageLayoutQuery,
  }

  const getPageLayoutQuery = (type: string = 'Home') => {
    return pageLayoutQuery[type as keyof typeof pageLayoutQuery]
  }

  const getSectionBySectionKey = (key: string): Section | undefined => {
    return getPageLayoutQuery(currentPage?.type)
      .data?.sectionsGroups.flatMap((block) => block.sections)
      .find(({ sectionKey }) => sectionKey === key) as Section
  }

  return {
    pageLayoutQuery: getPageLayoutQuery(currentPage?.type) || [],
    getSectionBySectionKey,
  }
}
